/**
 * Copyright © 2025 Delicious AI, LLC
 *
 * Client-side planogram vs snapshot alignment: infer shelf from box geometry,
 * then flag boxes whose product (or slot) does not match the planogram facing
 * at the same shelf / left-to-right index.
 */

import { isNil } from 'lodash'

import { ProductHelpers } from '@/utils/products.helpers'

/** Minimal facing shape from `planogramSegmentShapedFacings`. */
export type PlanogramFacingRef = {
  id: string
  upc: string
}

/** Bounding box fields used for shelf / slot inference. */
export type ShelfLayoutBoundingBox = {
  id: string
  minX: number
  minY: number
  maxX: number
  maxY: number
  classifiedLabel: string | null
  detectedLabel: string
}

const center = (min: number, max: number) => (min + max) / 2

/**
 * 1D k-means on Y centers to split boxes into `k` shelf clusters (horizontal bands).
 */
function kMeans1DAssign(values: number[], k: number, maxIter = 30): number[] {
  const n = values.length
  if (n === 0) return []
  if (k <= 1) return values.map(() => 0)

  const sorted = [...values].sort((a, b) => a - b)
  const centroids: number[] = []
  for (let i = 0; i < k; i++) {
    const idx = Math.min(
      sorted.length - 1,
      Math.floor(((i + 0.5) * sorted.length) / k),
    )
    centroids.push(sorted[idx])
  }

  const assignments = new Array<number>(n).fill(0)

  for (let iter = 0; iter < maxIter; iter++) {
    let changed = false
    for (let i = 0; i < n; i++) {
      const y = values[i]
      let best = 0
      let bestD = Infinity
      for (let c = 0; c < k; c++) {
        const d = Math.abs(y - centroids[c])
        if (d < bestD) {
          bestD = d
          best = c
        }
      }
      if (assignments[i] !== best) {
        assignments[i] = best
        changed = true
      }
    }

    const sums = new Array(k).fill(0)
    const counts = new Array(k).fill(0)
    for (let i = 0; i < n; i++) {
      const a = assignments[i]
      sums[a] += values[i]
      counts[a] += 1
    }
    for (let c = 0; c < k; c++) {
      if (counts[c] > 0) {
        centroids[c] = sums[c] / counts[c]
      }
    }

    if (!changed) break
  }

  return assignments
}

/**
 * Map k-means cluster indices to shelf order 0..k-1 where 0 is the top shelf
 * (smaller Y in image space).
 */
function orderClustersByCentroidY(
  values: number[],
  assignments: number[],
  k: number,
): number[] {
  const sums = new Array(k).fill(0)
  const counts = new Array(k).fill(0)
  for (let i = 0; i < values.length; i++) {
    const a = assignments[i]
    sums[a] += values[i]
    counts[a] += 1
  }
  const means = sums.map((s, c) => (counts[c] > 0 ? s / counts[c] : Infinity))
  const order = Array.from({ length: k }, (_, i) => i).sort(
    (a, b) => means[a] - means[b],
  )
  const clusterToShelf = new Array(k).fill(0)
  order.forEach((clusterIdx, shelfIdx) => {
    clusterToShelf[clusterIdx] = shelfIdx
  })
  return clusterToShelf
}

function effectiveUpc(box: ShelfLayoutBoundingBox): string | null {
  const raw = box.classifiedLabel ?? box.detectedLabel
  if (isNil(raw) || raw === '') return null
  return raw
}

function isLayoutNoiseBox(box: ShelfLayoutBoundingBox): boolean {
  const upc = effectiveUpc(box)
  return (
    upc === ProductHelpers.PRICING_UPC ||
    box.detectedLabel === ProductHelpers.PRICING_UPC
  )
}

/**
 * Returns snapshot bounding boxes that do not match the planogram at their
 * inferred shelf / horizontal slot (shelfPositions derived from Y clustering
 * and X ordering within each shelf).
 *
 * @param planogramSegmentShapedFacings — rows of facings (top shelf first), each row left-to-right
 * @param boundingBoxes — snapshot boxes (typically same set used for the compliance image)
 */
export function getIncorrectShelfBoundingBoxes(
  planogramSegmentShapedFacings: PlanogramFacingRef[][],
  boundingBoxes: ShelfLayoutBoundingBox[],
): ShelfLayoutBoundingBox[] {
  const shelves = planogramSegmentShapedFacings
  const k = shelves.length
  if (k === 0) return []

  const layoutBoxes = boundingBoxes.filter(b => !isLayoutNoiseBox(b))
  if (layoutBoxes.length === 0) return []

  const yCenters = layoutBoxes.map(b => center(b.minY, b.maxY))
  const assignments = kMeans1DAssign(yCenters, k)
  const clusterToShelf = orderClustersByCentroidY(yCenters, assignments, k)

  const shelfToBoxes: ShelfLayoutBoundingBox[][] = Array.from(
    { length: k },
    () => [],
  )
  for (let i = 0; i < layoutBoxes.length; i++) {
    const shelfIdx = clusterToShelf[assignments[i]]
    shelfToBoxes[shelfIdx].push(layoutBoxes[i])
  }

  for (let s = 0; s < k; s++) {
    shelfToBoxes[s].sort(
      (a, b) => center(a.minX, a.maxX) - center(b.minX, b.maxX),
    )
  }

  const incorrect: ShelfLayoutBoundingBox[] = []

  for (let s = 0; s < k; s++) {
    const facings = shelves[s] || []
    const boxesOnShelf = shelfToBoxes[s]
    const maxLen = Math.max(facings.length, boxesOnShelf.length)

    for (let i = 0; i < maxLen; i++) {
      const facing = facings[i]
      const box = boxesOnShelf[i]

      if (!facing && box) {
        incorrect.push(box)
        continue
      }
      if (facing && !box) {
        continue
      }
      if (!facing || !box) continue

      const expected = facing.upc
      const actual = effectiveUpc(box)

      if (actual === null) {
        if (expected !== ProductHelpers.UNKNOWN_UPC) {
          incorrect.push(box)
        }
        continue
      }

      if (actual === ProductHelpers.UNKNOWN_UPC) {
        if (
          expected !== ProductHelpers.UNKNOWN_UPC &&
          expected !== ProductHelpers.EMPTY_SLOT_UPC
        ) {
          incorrect.push(box)
        }
        continue
      }

      if (actual !== expected) {
        incorrect.push(box)
      }
    }
  }

  return incorrect
}
